import { useState, type FormEvent } from 'react';
import PageTransition from '../components/PageTransition';
import ScrollReveal from '../components/ScrollReveal';
import CTASection from '../components/CTASection';
import { useSEO } from '../hooks/useSEO';
import { PAGE_SEO } from '../utils/seo';

const projectTypes = ['Mobile App', 'Desktop App', 'Web Design', 'Logo Design', 'UI/UX Design', 'Reddit Design', 'Other'];
const budgetRanges = ['< $2k', '$2k-$10k', '$10k-$30k', '$30k+'];

const GOOGLE_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbzr4uUWf0QRFwOiiajH9ofz7qLOAQqmT8F5MS4c4whKZnBHROrYjjoN2DKQCoCs-krxgg/exec';

const faqItems = [
  {
    question: 'How long does it take to build an app?',
    answer: 'Most mobile apps take 4-8 weeks from concept to Play Store launch, depending on complexity and features. Simple utility apps can be faster, while complex platforms with backend infrastructure may take longer. We will give you a clear timeline during our initial consultation.',
  },
  {
    question: 'Do you publish apps to the Play Store / App Store?',
    answer: 'Yes. We handle the entire publishing process — from preparing store listings and screenshots to managing the submission and review process. You get a fully published, ready-to-download app.',
  },
  {
    question: 'Do you work with clients remotely?',
    answer: 'Absolutely. Most of our clients are remote. We use a combination of video calls, shared documents, and asynchronous communication to keep projects moving smoothly regardless of time zone.',
  },
  {
    question: 'What if I need updates after launch?',
    answer: 'Every project includes a post-launch support period. We also offer ongoing maintenance packages that include bug fixes, performance updates, OS compatibility, and new feature development.',
  },
];

type FormStatus = 'idle' | 'submitting' | 'success' | 'error';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    projectType: '',
    budget: '',
    message: '',
  });
  const [status, setStatus] = useState<FormStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  // Apply SEO
  useSEO(PAGE_SEO.contact);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMessage('');

    try {
      const body = new FormData();
      body.append('Name', formData.name);
      body.append('Email', formData.email);
      body.append('Project Type', formData.projectType);
      body.append('Budget', formData.budget);
      body.append('Message', formData.message);

      await fetch(GOOGLE_SCRIPT_URL, {
        method: 'POST',
        body,
      });

      // GAS handles CORS automatically for "Anyone" deployments
      setStatus('success');
      setFormData({ name: '', email: '', projectType: '', budget: '', message: '' });
    } catch {
      setStatus('error');
      setErrorMessage('Something went wrong. Please try again or email us directly.');
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    if (status === 'error') setStatus('idle');
  };

  return (
    <PageTransition>
      {/* Hero */}
      <section className="section">
        <div className="container">
          <ScrollReveal>
            <h1 className="h-1" style={{ marginBottom: 'var(--space-4)' }}>
              Get in Touch
            </h1>
            <p className="body-lg" style={{ color: 'var(--text-secondary)', maxWidth: '560px' }}>
              Tell us about your project. We will get back to you within 24 hours with a plan of action.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Contact Form + Info */}
      <ScrollReveal>
        <section className="section">
          <div className="container">
            <div className="grid-2">
              {/* Left: Form */}
              <div>
                {status === 'success' ? (
                  <div className="card" style={{ padding: 'var(--space-10)', textAlign: 'center', minHeight: '400px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-4)' }}>
                    <div
                      style={{
                        width: '64px',
                        height: '64px',
                        borderRadius: '50%',
                        background: 'rgba(30, 215, 96, 0.15)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: 'var(--space-2)',
                      }}
                    >
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--accent-green)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </div>
                    <h2 className="h-3" style={{ color: 'var(--accent-green)' }}>Message Sent!</h2>
                    <p className="body" style={{ color: 'var(--text-secondary)', maxWidth: '400px', lineHeight: 1.7 }}>
                      Thank you for reaching out. We&apos;ll review your project details and get back to you within 24 hours.
                    </p>
                    <button
                      className="btn btn--primary"
                      onClick={() => setStatus('idle')}
                    >
                      Send Another Message
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="contact-form" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                    <div>
                      <label htmlFor="name" className="caption-bold" style={{ display: 'block', marginBottom: 'var(--space-2)' }}>
                        Name
                      </label>
                      <input
                        id="name"
                        name="name"
                        type="text"
                        className="input"
                        placeholder="Your name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        disabled={status === 'submitting'}
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="caption-bold" style={{ display: 'block', marginBottom: 'var(--space-2)' }}>
                        Email
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        className="input"
                        placeholder="you@company.com"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        disabled={status === 'submitting'}
                      />
                    </div>
                    <div>
                      <label htmlFor="projectType" className="caption-bold" style={{ display: 'block', marginBottom: 'var(--space-2)' }}>
                        Project Type
                      </label>
                      <select
                        id="projectType"
                        name="projectType"
                        className="input"
                        value={formData.projectType}
                        onChange={handleChange}
                        required
                        disabled={status === 'submitting'}
                      >
                        <option value="">Select a type</option>
                        {projectTypes.map((type) => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label htmlFor="budget" className="caption-bold" style={{ display: 'block', marginBottom: 'var(--space-2)' }}>
                        Budget
                      </label>
                      <select
                        id="budget"
                        name="budget"
                        className="input"
                        value={formData.budget}
                        onChange={handleChange}
                        required
                        disabled={status === 'submitting'}
                      >
                        <option value="">Select a range</option>
                        {budgetRanges.map((range) => (
                          <option key={range} value={range}>
                            {range}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label htmlFor="message" className="caption-bold" style={{ display: 'block', marginBottom: 'var(--space-2)' }}>
                        Message
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        className="textarea"
                        placeholder="Tell us about your project, timeline, and any details that help us understand what you need."
                        rows={5}
                        value={formData.message}
                        onChange={handleChange}
                        required
                        disabled={status === 'submitting'}
                      />
                    </div>

                    {status === 'error' && (
                      <div style={{ padding: 'var(--space-3)', borderRadius: 'var(--radius-comfortable)', background: 'rgba(243, 114, 127, 0.1)', border: '1px solid rgba(243, 114, 127, 0.3)' }}>
                        <p className="caption" style={{ color: 'var(--text-negative)' }}>{errorMessage}</p>
                      </div>
                    )}

                    <button
                      type="submit"
                      className="btn btn--primary btn--large"
                      style={{ alignSelf: 'flex-start', marginTop: 'var(--space-2)' }}
                      disabled={status === 'submitting'}
                    >
                      {status === 'submitting' ? (
                        <>
                          <svg width="16" height="16" viewBox="0 0 24 24" style={{ animation: 'spin 1s linear infinite' }}>
                            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" fill="none" strokeDasharray="31.4 31.4" strokeLinecap="round" />
                          </svg>
                          Sending...
                        </>
                      ) : (
                        <>
                          Send Message
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="22" y1="2" x2="11" y2="13" />
                            <polygon points="22 2 15 22 11 13 2 9 22 2" />
                          </svg>
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>

              {/* Right: Contact Info */}
              <div className="contact-info">
                <div className="card" style={{ padding: 'var(--space-6)', marginBottom: 'var(--space-4)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginBottom: 'var(--space-5)' }}>
                    <div style={{ width: '40px', height: '40px', borderRadius: 'var(--radius-comfortable)', background: 'rgba(30, 215, 96, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--accent-green)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                        <polyline points="22,6 12,13 2,6" />
                      </svg>
                    </div>
                    <div>
                      <p className="caption-bold">Email</p>
                      <p className="body" style={{ color: 'var(--accent-green)' }}>hello@siak.in</p>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginBottom: 'var(--space-5)' }}>
                    <div style={{ width: '40px', height: '40px', borderRadius: 'var(--radius-comfortable)', background: 'rgba(83, 157, 245, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--text-announcement)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                        <circle cx="12" cy="10" r="3" />
                      </svg>
                    </div>
                    <div>
                      <p className="caption-bold">Location</p>
                      <p className="body" style={{ color: 'var(--text-near-white)' }}>Remote-first, worldwide</p>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                    <div style={{ width: '40px', height: '40px', borderRadius: 'var(--radius-comfortable)', background: 'rgba(255, 164, 43, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--text-warning)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12 6 12 12 16 14" />
                      </svg>
                    </div>
                    <div>
                      <p className="caption-bold">Response Time</p>
                      <p className="body" style={{ color: 'var(--text-near-white)' }}>We typically respond within 24 hours</p>
                    </div>
                  </div>
                </div>

                {/* Social Links */}
                <div className="card" style={{ padding: 'var(--space-5)' }}>
                  <p className="caption-bold" style={{ marginBottom: 'var(--space-3)' }}>Follow Us</p>
                  <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
                    <a href="#" className="btn btn--outlined btn--small" aria-label="Twitter / X">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                      </svg>
                      X
                    </a>
                    <a href="#" className="btn btn--outlined btn--small" aria-label="Instagram">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                        <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                        <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
                      </svg>
                      Instagram
                    </a>
                    <a href="#" className="btn btn--outlined btn--small" aria-label="LinkedIn">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
                        <rect x="2" y="9" width="4" height="12" />
                        <circle cx="4" cy="4" r="2" />
                      </svg>
                      LinkedIn
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* FAQ Section */}
      <ScrollReveal>
        <section className="section">
          <div className="container">
            <h2 className="h-2" style={{ marginBottom: 'var(--space-8)' }}>
              Frequently Asked Questions
            </h2>
            <div style={{ maxWidth: '800px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
              {faqItems.map((item, i) => (
                <details
                  key={i}
                  className="faq-item card"
                  style={{ padding: 'var(--space-5)', backgroundColor: 'var(--bg-surface)', cursor: 'pointer' }}
                >
                  <summary className="body-bold" style={{ listStyle: 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-4)' }}>
                    {item.question}
                    <svg
                      className="faq-icon"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ flexShrink: 0, transition: 'transform 0.2s ease' }}
                    >
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                  </summary>
                  <p
                    className="caption"
                    style={{
                      color: 'var(--text-secondary)',
                      lineHeight: 1.65,
                      marginTop: 'var(--space-3)',
                      paddingTop: 'var(--space-3)',
                      borderTop: '1px solid var(--separator)',
                    }}
                  >
                    {item.answer}
                  </p>
                </details>
              ))}
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* CTA */}
      <ScrollReveal>
        <section className="section">
          <div className="container">
            <CTASection
              title="Ready to start?"
              subtitle="The best projects begin with a conversation. Let us hear what you are building."
              primaryLabel="Start a Project"
              secondaryLabel="View Services"
              primaryHref="/contact"
              secondaryHref="/services"
            />
          </div>
        </section>
      </ScrollReveal>
    </PageTransition>
  );
}
